import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, Hourglass, Ticket } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Button, Field, Input } from "@/design";
import { normaliseInviteCode, type SignupConfig } from "@/lib/api";
import { docsUrl } from "@/lib/config";
import { OnboardingFrame, StepHeading } from "./OnboardingFrame";

/** The marketing site's waitlist page, with the email filled in when we have it. */
export function waitlistHref(email?: string): string {
  const base = docsUrl("/waitlist");
  if (!email) return base;
  try {
    const u = new URL(base);
    u.searchParams.set("email", email);
    return u.toString();
  } catch {
    return base;
  }
}

/** Shown instead of the sign-up form while sign-ups are closed or invite-only. */
export function Waitlist({ config, email }: { config: SignupConfig; email?: string }) {
  const closed = config.mode === "closed";
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [touched, setTouched] = useState(false);
  const clean = normaliseInviteCode(code);

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (clean.length < 4) return;
    void navigate({ to: "/invite/$code", params: { code: code.trim() } });
  };

  return (
    <OnboardingFrame
      step={null}
      panel={
        <>
          <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white">We're opening the doors carefully.</h2>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/70">
            Every account runs on real servers in the UK. We let people in a few at a time so the platform stays fast and safe for everyone on it.
          </p>
        </>
      }
    >
      <span className="mb-5 flex size-12 items-center justify-center rounded-2xl bg-accent-soft text-accent-text" aria-hidden="true">
        <Hourglass className="size-6" />
      </span>
      <StepHeading title={closed ? "Sign-ups are closed for now" : "Guv is invite-only for now"}>
        {closed
          ? "We're not taking new accounts at the moment. Join the waitlist and we'll email you an invitation as soon as there's room."
          : "Join the waitlist and we'll email you an invitation code when it's your turn."}
      </StepHeading>
      <div className="mt-8 space-y-3">
        {config.waitlist_open && (
          <Button asChild variant="primary" size="lg" className="w-full">
            <a href={waitlistHref(email)}>
              Join the waitlist
              <ArrowUpRight aria-hidden="true" />
            </a>
          </Button>
        )}
        <Button asChild size="lg" className="w-full">
          <Link to="/login">I already have an account</Link>
        </Button>
      </div>

      {!closed && (
        <form onSubmit={submitCode} noValidate className="mt-8 border-t border-border pt-6">
          <div className="flex items-start gap-2">
            <Field label="Got an invitation code?" className="min-w-0 flex-1" error={touched && clean.length < 4 ? "Enter the code from your invitation email." : undefined}>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="ABCD-EFGH"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                leading={<Ticket />}
                className="font-mono uppercase"
              />
            </Field>
            {/* Lines up with the input under the label. */}
            <Button type="submit" className="mt-[26px]">
              Use code
            </Button>
          </div>
          <p className="mt-2 text-sm text-muted">Invited to a team? Open the link in that email instead: it takes you straight to them.</p>
        </form>
      )}
    </OnboardingFrame>
  );
}
